<template>
  <div
    :key="$route.fullPath"
    class="wrap"
  >
    <div
      v-if="!loaded"
      class="wrap--loading"
    />

    <div
      v-else
      key="DATASET"
    >
      <div
        v-if="isDownloadingImage"
        key="dataset_image"
        class="dataset"
      >
        <div
          class="dataset__image_download"
        >
          <div class="dataset__title">
            <h1>Chart image download preview</h1>
          </div>

          <div class="dataset__close">
            <UtilityIconButton
              size="md"
              content="Close"
              v-tippy="{ theme: 'statzon', placement: 'left', arrow: true, animation: 'scale' }"
              @click.native="$store.dispatch('app/setIsDownloadingImage', false)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" height="100%" width="100%" viewBox="0 0 24 24"><path d="M23.954 21.03l-9.184-9.095 9.092-9.174-2.832-2.807-9.09 9.179-9.176-9.088-2.81 2.81 9.186 9.105-9.095 9.184 2.81 2.81 9.112-9.192 9.18 9.1z" /></svg>
            </UtilityIconButton>
          </div>

          <div class="dataset__controls">
            <LayoutImageControls />
          </div>

          <FormSubmit
            class="dataset__image_download_button"
            icon='<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path d="M16 11h5l-9 10-9-10h5v-11h8v11zm1 11h-10v2h10v-2z"/></svg>'
            @click="downloadImage()"
          >
            Download PNG
          </FormSubmit>

          <br />

          <div
            class="dataset__toggle"
          >
            <FormSwitch
              v-model="showUnselectedDatasets"
            />
            <span>Show unselected datasets</span>
          </div>

          <p class="dataset__toggle_info">You can add/remove datasets to/from image by clicking dataset names in the legend.</p>

          <div
            v-if="selectedDatasetControls.chart_type === 'pie'"
            class="chart__timeline"
          >
            <UtilitySlider
              variant="timeline"
              :items="imageTimelineItems"
              @toggledItem="toggleTimeline"
            />
          </div>

          <div class="dataset__imagewrap">
            <div class="dataset__imagewrap_scroll">
              <div
                id="dataset_image"
              >
                <div class="dataset__title">
                  <h1>{{ results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.title ? results.dataset.byId[selectedHash].attributes.title : '' }} {{ selectedDatasetControls.chart_type === 'pie' ? ('[' + selectedChartTimeline + ']') : '' }}</h1>
                </div>

                <ChartImage
                  id="image_chart"
                  :key="settings.dataset.display + results + unselectedImageLabels.length + selectedChartTimeline + selectedDatasetControls.chart_type"
                  :data="imageDataSeries"
                  :title="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.title ? results.dataset.byId[selectedHash].attributes.title : ''"
                  :scale="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.scales[0].attributes.name ? results.dataset.byId[selectedHash].attributes.scales[0].attributes.name : ''"
                />

                <div
                  v-if="Object.keys(imageDataLegend).length > 1"
                  class="dataset__image_legend"
                >
                  <div class="image_legend__items">
                    <div
                      v-for="label in Object.keys(imageDataLegend)"
                      :key="'LABEL__' + label"
                      :class="['image_legend__item', unselectedImageLabels.indexOf(label) !== -1 ? 'image_legend__item--inactive' : 'image_legend__item--active']"
                      @click="toggleLabel(label)"
                    >
                      <div
                        class="image_legend__icon"
                        :style="'background-color: ' + imageDataLegend[label].color + ';'"
                      />
                      <div class="image_legend__name">{{ label }}</div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="showSource"
                  class="dataset__source"
                >
                  <span class="dataset__attribute">Source: </span><span class="dataset__value">Statzon {{ results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.providers[0].attributes.name && results.dataset.byId[selectedHash].attributes.providers[0].attributes.name !== '' && results.dataset.byId[selectedHash].attributes.providers[0].attributes.name !== 'Statzon' ? (' / ' + results.dataset.byId[selectedHash].attributes.providers[0].attributes.name) : '' }}</span>
                </div>
              </div>
            </div>
          </div>
          <a id="image_download_link"></a>
        </div>
      </div>

      <div
        v-else
        key="dataset_chartView"
        class="dataset"
      >
        <LayoutResourceHeader
          :title="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.title ? results.dataset.byId[selectedHash].attributes.title : 'Unknown'"
          type="datasets"
          logo='<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M5 19h-4v-4h4v4zm6 0h-4v-8h4v8zm6 0h-4v-13h4v13zm6 0h-4v-19h4v19zm1 2h-24v2h24v-2z"></path></svg>'
          :topics="[results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.topics[0] ? results.dataset.byId[selectedHash].attributes.topics[0].id : '']"
          :locationId="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.locations[0] ? results.dataset.byId[selectedHash].attributes.locations[0].id : ''"
          :resourceId="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].id ? results.dataset.byId[selectedHash].id : '#'"
          :licensed="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.licensed ? results.dataset.byId[selectedHash].attributes.licensed : false"
          :trial="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.trial ? results.dataset.byId[selectedHash].attributes.trial : false"
          :free="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.free ? results.dataset.byId[selectedHash].attributes.free : false"
          :viewed="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.viewed ? results.dataset.byId[selectedHash].attributes.viewed : false"
          :downloaded="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.downloaded ? results.dataset.byId[selectedHash].attributes.downloaded : false"
          :filterWords="filterWords"
          :comingFrom="comingFrom"
        />

        <div class="dataset__chart_view">
          <div
            class="dataset__controls"
            id="dataset__controls"
          >
            <LayoutFilterControls
              type="dataset"
              :selectedTopic="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.topics ? results.dataset.byId[selectedHash].attributes.topics[0] : false"
              :selectedLocation="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.locations ? results.dataset.byId[selectedHash].attributes.locations[0] : false"
              :selectedTradeType="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.trade_types ? results.dataset.byId[selectedHash].attributes.trade_types[0] : false"
              :selectedScale="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.scales ? results.dataset.byId[selectedHash].attributes.scales[0] : false"
            />

            <LayoutControls
              :controls="controls"
              layout="maximized"
              variant="light"
              id="dataset__chartControls"
              @controlsChanged="toggleControls"
              :licensed="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.licensed"
            />
          </div>

          <div
            v-show="settings.dataset.display === 'datatable'"
            key="DATASET_DATATABLE"
            class="dataset__datatable"
          >
            <UtilityDataTable
              :data="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.data_series"
              :scale="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.scales[0].attributes.name"
            />
          </div>

          <div
            v-show="results && results.dataset && results.dataset.byId[selectedHash] && settings.dataset.display !== 'datatable'"
            key="DATASET_CHART"
            class="dataset__chart"
          >
            <ChartFull />
          </div>

          <div class="dataset__content">
            <LayoutResourceInfo
              :resource="results && results.dataset && results.dataset.byId[selectedHash] ? results.dataset.byId[selectedHash] : false"
              :relatedPdf="results.datasetPdf ? results.datasetPdf.byId[results.datasetPdf.allIds[0]] : false"
              :loading="loading.datasetPdf"
              :timeout="timeout.datasetPdf"
              :noResults="noResults.datasetPdf"
            />

            <UtilityTabs>
              <UtilityTab
                key="DATASET_TAB__0"
                label="Data Provider"
                :index="0"
                :selected="tabIndex"
                :disabled="false"
                @tabChanged="changeTab"
              />

              <UtilityTab
                v-for="tab in tabs"
                :key="'DATASET_TAB__' + tab.index"
                :label="tab.label"
                :index="tab.index"
                :selected="tabIndex"
                :disabled="tab.disabled"
                @tabChanged="changeTab"
              />

              <!--<UtilityTab
                key="DATASET_TAB__1"
                label="Definition"
                :index="1"
                :selected="tabIndex"
                :disabled="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.definition ? false : true"
                @tabChanged="changeTab"
              />

              <UtilityTab
                key="DATASET_TAB__2"
                label="Methodology"
                :index="2"
                :selected="tabIndex"
                :disabled="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.methodology ? false : true"
                @tabChanged="changeTab"
              />

              <UtilityTab
                key="DATASET_TAB__3"
                label="Summary"
                :index="3"
                :selected="tabIndex"
                :disabled="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.summary ? false : true"
                @tabChanged="changeTab"
              />
              -->
            </UtilityTabs>

            <UtilityTabPanel :index="0" :selected="tabIndex">
              <LayoutDataProvider
                :id="results && results.dataset && results.dataset.byId[selectedHash] ? results.dataset.byId[selectedHash].attributes.providers[0].id : false"
                :name="results && results.dataset && results.dataset.byId[selectedHash] ? results.dataset.byId[selectedHash].attributes.providers[0].attributes.name : false"
                :website="results && results.dataset && results.dataset.byId[selectedHash] ? results.dataset.byId[selectedHash].attributes.providers[0].attributes.website : false"
                :description="results && results.dataset && results.dataset.byId[selectedHash] ? results.dataset.byId[selectedHash].attributes.providers[0].attributes.description : false"
              />
            </UtilityTabPanel>

            <UtilityTabPanel
              v-for="tab in tabs"
              :index=tab.index
              :selected=tabIndex
            >
              <div
                v-if="tab.content"
                v-html="highlight(links(tab.content), filterWords)"
              />
              <div
                v-else
                v-html="tab.label + ' is not available.'"
              />
            </UtilityTabPanel>

            <!--<UtilityTabPanel :index="1" :selected="tabIndex">
              <div
                v-if="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.definition"
                v-html="highlight(links(results.dataset.byId[selectedHash].attributes.definition), filterWords)"
              />
              <div
                v-else
                v-html="'Definition is not available.'"
              />
            </UtilityTabPanel>

            <UtilityTabPanel :index="2" :selected="tabIndex">
              <div
                v-if="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.methodology"
                v-html="highlight(links(results.dataset.byId[selectedHash].attributes.methodology), filterWords)"
              />
              <div
                v-else
                v-html="'Methodology is not available.'"
              />
            </UtilityTabPanel>

            <UtilityTabPanel :index="3" :selected="tabIndex">
              <div
                v-if="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.summary"
                v-html="highlight(links(results.dataset.byId[selectedHash].attributes.summary), filterWords)"
              />
              <div
                v-else
                v-html="'Summary is not available.'"
              />
            </UtilityTabPanel>-->
          </div>

          <div class="dataset__meta">
            <AppDatasets
              key="DATASET_TOPICS"
              type="related"
              sliderName="dataset_topics"
              identifier="datasetTopics"
              :title="windowWidth < 960 ? 'Datasets with topic' : 'More datasets with topic'"
              :subtitle="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.topics[0] ? results.dataset.byId[selectedHash].attributes.topics[0].attributes.name : false"
              display="slider"
              :selectedResultId="selectedId"
              :results="results.datasetTopics"
              :paginator="meta.datasetTopics.paginator"
              :loading="loading.datasetTopics"
              :timeout="timeout.datasetTopics"
              :noResults="noResults.datasetTopics"
              @itemClicked="itemClicked"
              @controlsChanged="toggleControls"
            />

            <AppDatasets
              key="DATASET_PROVIDERS"
              type="related"
              sliderName="dataset_providers"
              identifier="datasetProviders"
              :title="windowWidth < 960 ? 'Datasets from' : 'More datasets from'"
              :subtitle="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.providers[0] ? results.dataset.byId[selectedHash].attributes.providers[0].attributes.name : false"
              subtitleColor="pink"
              display="slider"
              :selectedResultId="selectedId"
              :results="results.datasetProviders"
              :paginator="meta.datasetProviders.paginator"
              :loading="loading.datasetProviders"
              :timeout="timeout.datasetProviders"
              :noResults="noResults.datasetProviders"
              @itemClicked="itemClicked"
              @controlsChanged="toggleControls"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppDatasets from '@/components/app/Datasets.vue'
import LayoutResourceHeader from '@/components/layout/ResourceHeader.vue'
import LayoutControls from '@/components/layout/Controls.vue'
import LayoutImageControls from '@/components/layout/ImageControls.vue'
import LayoutFilterControls from '@/components/layout/FilterControls.vue'
import LayoutResourceInfo from '@/components/layout/ResourceInfo.vue'
import LayoutDataProvider from '@/components/layout/DataProvider.vue'
import ChartFull from '@/components/chart/Full.vue'
import ChartImage from '@/components/chart/Image.vue'
import FormSubmit from '@/components/form/Submit.vue'
import FormSwitch from '@/components/form/Switch.vue'
import UtilityIconButton from '@/components/utility/IconButton.vue'
import UtilityDataTable from '@/components/utility/DataTable.vue'
import UtilitySlider from '@/components/utility/Slider.vue'
import UtilityTabs from '@/components/utility/Tabs.vue'
import UtilityTab from '@/components/utility/Tab.vue'
import UtilityTabPanel from '@/components/utility/TabPanel.vue'
import helperMethods from '@/helpers/utility.js'
import html2canvas from 'html2canvas'
import { downloadPost } from '@/helpers/http'

export default {
  components: {
    AppDatasets,
    LayoutResourceHeader,
    LayoutControls,
    LayoutImageControls,
    LayoutFilterControls,
    LayoutResourceInfo,
    LayoutDataProvider,
    ChartFull,
    ChartImage,
    FormSubmit,
    FormSwitch,
    UtilityIconButton,
    UtilityDataTable,
    UtilitySlider,
    UtilityTabs,
    UtilityTab,
    UtilityTabPanel
  },
  metaInfo () {
    if (this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes) {
      return {
        title: this.results.dataset.byId[this.selectedHash].attributes.title,
        meta: [
          {
            name: 'description',
            content: this.results.dataset.byId[this.selectedHash].attributes.definition
          }
        ],
        /*link: [
          {
            rel: 'canonical', href: 'https://app.statzon.com/datasets/' + this.selectedHash
          }
        ]*/
      }
    } else {
      return {}
    }
  },
  data () {
    return {
      windowWidth: window.innerWidth,
      loaded: false,
      loading: {
        datasetTopics: true,
        datasetProviders: true,
        datasetPdf: true
      },
      timeout: {
        datasetTopics: false,
        datasetProviders: false,
        datasetPdf: false
      },
      noResults: {
        datasetTopics: false,
        datasetProviders: false,
        datasetPdf: false
      },
      url: {
        datasetTopics: false,
        datasetProviders: false,
        datasetPdf: false
      },
      comingFrom: false,
      panelSuccess: false,
      showSource: true,
      showUnselectedDatasets: false,
      legendRows: 2,
      tabIndex: 0,
      unselectedImageLabels: [],
      colors: [
        '#1593A6',
        '#8BC8D3',
        '#6BBB5D',
        '#BCDE85',
        '#E18731',
        '#F5AC91',
        '#A86CC5',
        '#B4ACE3',
        '#DF5D99',
        '#DEA1D1',
        '#48AA9F',
        '#96D1B4',
        '#EE7447',
        '#F4A3A0'
      ],
      controls: {
        'maximized': [{
          'group': [{
            'name': 'dataset.display',
            'value': ['ver_bar', 'hor_bar'],
            'label': ['Display in <u>vertical bar</u> chart', 'Display in <u>horizontal bar</u> chart'],
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M5 19h-4v-8h4v8zm6 0h-4v-18h4v18zm6 0h-4v-12h4v12zm6 0h-4v-4h4v4zm1 2h-24v2h24v-2z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          },{
            'name': 'dataset.display',
            'value': ['stacked_ver_bar', 'stacked_hor_bar'],
            'label': ['Display in <u>stacked</u> <u>vertical bar</u> chart', 'Display in <u>stacked</u> <u>horizontal bar</u> chart'],
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M4 17.162l-2 .838v-12.972l12-5.028v2.507l-10 4.19v10.465zm18-11.162l-12 5.028v12.972l12-5.028v-12.972zm-14 3.697l10-4.19v-2.507l-12 5.028v12.972l2-.838v-10.465z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          },{
            'name': 'dataset.display',
            'value': ['hor_line', 'ver_line'],
            'label': ['Display in <u>horizontal line</u> chart', 'Display in <u>vertical line</u> chart'],
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill-rule="evenodd" clip-rule="evenodd"><path d="M18.799 7.038c-.496-.535-.799-1.252-.799-2.038 0-1.656 1.344-3 3-3s3 1.344 3 3-1.344 3-3 3c-.146 0-.29-.01-.431-.031l-3.333 6.032c.475.53.764 1.231.764 1.999 0 1.656-1.344 3-3 3s-3-1.344-3-3c0-.583.167-1.127.455-1.587l-2.565-3.547c-.281.087-.58.134-.89.134l-.368-.022-3.355 6.069c.451.525.723 1.208.723 1.953 0 1.656-1.344 3-3 3s-3-1.344-3-3 1.344-3 3-3c.186 0 .367.017.543.049l3.298-5.967c-.52-.539-.841-1.273-.841-2.082 0-1.656 1.344-3 3-3s3 1.344 3 3c0 .617-.187 1.191-.507 1.669l2.527 3.495c.307-.106.637-.164.98-.164.164 0 .325.013.482.039l3.317-6.001zm-3.799 7.962c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1zm-6-8c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          },{
            'name': 'dataset.display',
            'value': 'pie',
            'label': 'Display in <u>pie</u> chart',
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M13.012 5.007v-1.668l2.802-2.771c.409.136.809.293 1.197.471l-3.999 3.968zm7.089-1.93l-7.089 7.058v.853h.877l7.044-7.076c-.263-.292-.541-.57-.832-.835zm-7.089-1.468l1.437-1.406c-.46-.094-.96-.163-1.437-.203v1.609zm10.789 7.962l-1.386 1.417h1.585c-.04-.47-.106-.964-.199-1.417zm-.363-1.366c-.135-.41-.292-.81-.469-1.199l-3.951 3.982h1.668l2.752-2.783zm-1.063-2.337c-.205-.346-.426-.682-.664-1.004l-6.093 6.124h1.668l5.089-5.12zm-3.225-3.57c-.322-.238-.657-.459-1.003-.665l-5.135 5.104v1.668l6.138-6.107zm-8.15 10.702v-13c-6.161.519-11 5.683-11 11.978 0 6.639 5.382 12.022 12.021 12.022 6.296 0 11.46-4.839 11.979-11h-13z"/></svg>',
            'iconSize': 'md',
            'color': 'blue'
          },{
            'name': 'dataset.display',
            'value': 'datatable',
            'label': 'Display in <u>data table</u>',
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M17 13h-10v-1h10v1zm0 2h-10v1h10v-1zm0 3h-10v1h10v-1zm5-16v22h-20v-22h3v1c0 1.103.897 2 2 2s2-.897 2-2v-1h6v1c0 1.103.897 2 2 2s2-.897 2-2v-1h3zm-2 7h-16v13h16v-13zm-12-8c0-.552-.447-1-1-1s-1 .448-1 1v2c0 .552.447 1 1 1s1-.448 1-1v-2zm10 0c0-.552-.447-1-1-1s-1 .448-1 1v2c0 .552.447 1 1 1s1-.448 1-1v-2z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          }]
        },{
          'group': [{
            'name': 'datasets.project',
            'value': 'Dataset',
            'label': 'Save Dataset to <u>Project</u>',
            'icon': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M11 5c-1.629 0-2.305-1.058-4-3h-7v20h24v-17h-13zm3.599 12l-2.599-1.39-2.599 1.39.518-2.902-2.125-2.042 2.921-.404 1.285-2.652 1.285 2.652 2.921.404-2.125 2.042.518 2.902z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          }]
        },{
          'group': [{
            'name': 'dataset.download',
            'value': 'img',
            'label': 'Download as <u>PNG</u> image',
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M14.568.075c2.202 1.174 5.938 4.883 7.432 6.881-1.286-.9-4.044-1.657-6.091-1.179.222-1.468-.185-4.534-1.341-5.702zm7.432 10.925v13h-20v-24h8.409c4.857 0 3.335 8 3.335 8 3.009-.745 8.256-.419 8.256 3zm-15 1.5c0 .828.672 1.5 1.501 1.5.827 0 1.499-.672 1.499-1.5s-.672-1.5-1.499-1.5c-.829 0-1.501.672-1.501 1.5zm10 6.5l-3.5-6-2.093 2.968-1.31-.968-3.097 4h10z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          },{
            'name': 'dataset.download',
            'value': 'xls',
            'label': 'Download as <u>XLS</u> document',
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M14.568.075c2.202 1.174 5.938 4.883 7.432 6.881-1.286-.9-4.044-1.657-6.091-1.179.222-1.468-.185-4.534-1.341-5.702zm7.432 10.925v13h-20v-24h8.409c4.857 0 3.335 8 3.335 8 3.009-.745 8.256-.419 8.256 3zm-16 5h5v-4h-5v4zm12 2h-12v1h12v-1zm0-3h-5v1h5v-1zm0-3h-5v1h5v-1z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          },{
            'name': 'dataset.embed',
            'value': 'embed',
            'label': 'Embed dataset',
            'icon': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 122.88 76.5"><path d="M47.53,61.26l.15.15a8.91,8.91,0,0,1-.18,12.46s-.11.13-.16.16a8.91,8.91,0,0,1-12.42-.1c-9.66-8.86-23.59-20-32.1-29.25a8.92,8.92,0,0,1,.11-13.12c5.41-6.71,24.44-22.15,32-29A8.93,8.93,0,0,1,49.12,13a1.66,1.66,0,0,1-.44.56l-27,24.78c4.32,4.19,8.09,7.31,11.86,10.43a172.63,172.63,0,0,1,14,12.48Zm27.82,0-.15.15a8.91,8.91,0,0,0,.18,12.46s.11.13.16.16A8.91,8.91,0,0,0,88,73.93c9.66-8.86,23.59-20,32.1-29.25A8.92,8.92,0,0,0,120,31.56c-5.41-6.71-24.44-22.15-32-29A8.93,8.93,0,0,0,73.76,13a1.77,1.77,0,0,0,.43.56l27,24.78c-4.32,4.19-8.1,7.31-11.86,10.43a172.63,172.63,0,0,0-14,12.48Z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          }]
        }]
      }
    }
  },
  computed: {
    userAuthenticated () {
      return this.$store.getters['user/isAuthenticated']
    },
    selectedHash () {
      return this.$store.getters['search/getSelectedHash']
    },
    selectedId () {
      return this.$store.getters['search/getSelectedId']
    },
    results () {
      return this.$store.getters['search/getResults']
    },
    searchToken () {
      return this.$store.getters['search/getSearchToken']
    },
    settings () {
      return this.$store.getters['app/getSettings']
    },
    input () {
      return this.$store.getters['search/getInput']
    },
    selectedDatasetControls () {
      return this.$store.getters['search/getSelectedDatasetControls']
    },
    selectedChartTimeline () {
      return this.$store.getters['search/getSelectedChartTimeline']
    },
    meta () {
      return this.$store.getters['search/getMeta']
    },
    imageDataLegend () {
      let series = {}
      let dataSeries = JSON.parse(JSON.stringify(this.results.dataset.byId[this.selectedHash].attributes.data_series))
      let unselected = this.unselectedImageLabels
      let selectedCount = 0
      let that = this
      if (this.showUnselectedDatasets) {
        Object.keys(dataSeries).forEach(function (serie) {
          series[serie] = dataSeries[serie]
          if (unselected.indexOf(serie) === -1) {
            series[serie]['color'] = that.colors[selectedCount]
            selectedCount++
            if (selectedCount >= that.colors.length) {
              selectedCount = 0
            }
          } else {
            series[serie]['color'] = '#888'
          }
        })
      } else {
        Object.keys(dataSeries).forEach(function (serie, index) {
          if (unselected.indexOf(serie) === -1) {
            series[serie] = dataSeries[serie]
            series[serie]['color'] = that.colors[selectedCount]
            selectedCount++
            if (selectedCount >= that.colors.length) {
              selectedCount = 0
            }
          }
        })
      }
      return series
    },
    imageDataSeries () {
      let series = {}
      let dataSeries = JSON.parse(JSON.stringify(this.results.dataset.byId[this.selectedHash].attributes.data_series))
      let unselected = this.unselectedImageLabels
      let selectedCount = 0
      let that = this
      Object.keys(dataSeries).forEach(function (serie) {
        if (unselected.indexOf(serie) === -1) {
          series[serie] = dataSeries[serie]
        }
      })
      return series
    },
    imageTimelineItems () {
      let dataSeries = JSON.parse(JSON.stringify(this.results.dataset.byId[this.selectedHash].attributes.data_series))
      let keys = []
      let items = []

      Object.keys(dataSeries).forEach(function (key) {
        Object.keys(dataSeries[key]).forEach(function (item) {
          if (keys.indexOf(item) === -1) {
            keys.push(item)
          }
        })
      })
      keys.sort()

      keys.forEach(function (key) {
        items.push({
          name: key,
          disabled: false
        })
      })

      return items
    },
    filterWords () {
      let words = []
      let getter = this.$store.getters['search/getSelectedFilters']

      Object.keys(getter).forEach(function (filters) {
        if (getter[filters] && getter[filters].length > 0) {
          getter[filters].forEach(function (filter) {
            words.push(filter.attributes.name)
          })
        }
      })

      return words
    },
    tabs () {
      let tabs = []
      let index = 1

      if (this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.definition) {
        tabs.push({
          'index': index,
          'label': 'Definition',
          'content': this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.definition ? this.highlight(this.links(this.results.dataset.byId[this.selectedHash].attributes.definition), this.filterWords) : 'Definition is not available.',
          'disabled': this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.definition ? false : true
        })

        index++
      }

      if (this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.methodology) {
        tabs.push({
          'index': index,
          'label': 'Methodology',
          'content': this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.methodology ? this.highlight(this.links(this.results.dataset.byId[this.selectedHash].attributes.methodology), this.filterWords) : 'Methodology is not available.',
          'disabled': this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.methodology ? false : true
        })

        index++
      }

      if (this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.summary) {
        tabs.push({
          'index': index,
          'label': 'Summary',
          'content': this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.summary ? this.highlight(this.links(this.results.dataset.byId[this.selectedHash].attributes.summary), this.filterWords) : 'Summary is not available.',
          'disabled': this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.summary ? false : true
        })
      }

      let metaItems = this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.meta ? this.results.dataset.byId[this.selectedHash].attributes.meta : false
      
      if (metaItems) {
        metaItems = Object.entries(JSON.parse(metaItems))

        for (const [key, value] of metaItems) {
          tabs.push({
            'index': index,
            'label': key,
            'content': value,
            'disabled': false
          })
          index++;
        }
      }

      return tabs
    },
    publishedDates () {
      let publishedStatzon = ''
      let publishedOriginal = ''

      if (this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.created_at) {
        publishedStatzon = new Date(this.results.dataset.byId[this.selectedHash].attributes.created_at).toUTCString() + ' <span class="text--light">(@ Statzon)</span>'
      }

      if (this.results && this.results.dataset && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.published_at) {
        publishedOriginal = '<br>' + this.results.dataset.byId[this.selectedHash].attributes.published_at + ' <span class="text--light">(@ original source)</span>'
      }

      return publishedStatzon + publishedOriginal
    },
    isDownloadingImage () {
      return this.$store.getters['app/getIsDownloadingImage']
    },
    isAuthenticated () {
      return this.$store.getters['user/isAuthenticated']
    }
  },
  methods: {
    addEmbed () {
      this.$store.dispatch('search/setAddingEmbed', true)
    },
    search (type, view, url) {
      this.$store.dispatch('search/setSearchToken', { 'type': view, 'url': url })
      url = url + 'search_token=' + this.searchToken[view]

      if (!this.results[type] || this.searchToken[view] !== this.meta[type].parameters.search_token) {
        this.$store.dispatch('search/fetchResults', { 'type': type, 'view': view, 'url': url, preventGa: true }).then(response => {
          let checkResults = this.results[type]
          this.loading[type] = false

          if (checkResults.allIds.length === 0) {
            this.noResults[type] = true
            this.timeout[type] = false
          } else {
            this.noResults[type] = false
            this.timeout[type] = false
          }

          return true
        }).catch(error => {
          this.timeout[type] = true
          this.loading[type] = false
        })
      } else {
        this.noResults[type] = false
        this.timeout[type] = false
        this.loading[type] = false
      }

      return false
    },
    downloadImage () {
      let canvasEl = document.getElementById('dataset_image')
      let filename = this.$store.getters['search/getResults'].dataset.byId[this.$store.getters['search/getSelectedHash']].attributes.title.replace(/ /g, '_').toLowerCase()
      let downloadLink = document.getElementById('image_download_link')

      if (canvasEl) {
        let that = this
        html2canvas(canvasEl).then(function (canvas) {
          canvas.setAttribute('id', 'htmlcanvas')
          canvasEl.appendChild(canvas)
          let dt = document.getElementById('htmlcanvas').toDataURL('image/png')
          downloadLink.setAttribute('href', dt)
          downloadLink.setAttribute('download', filename)
          downloadLink.click()
          downloadLink.removeAttribute('href')
          downloadLink.removeAttribute('download')
          canvas.remove()
          that.$store.dispatch('app/setIsDownloadingImage', false)
        })

        this.sendGoogleAnalyticsEvent('download_datasets_image', this.$store.getters['search/getResults'].dataset.byId[this.$store.getters['search/getSelectedHash']].attributes.title, false, this.$store.getters['user/getSubscriptionStatus'])
      }
    },
    downloadExcel () {
      let parameters = {
        datasets: [this.$store.getters['search/getSelectedId']]
      }

      downloadPost('downloads/dataset', parameters).then(response => {
        let blob = new Blob([response.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        let chartTitle = this.$store.getters['search/getResults'].dataset.byId[this.$store.getters['search/getSelectedHash']].attributes.title.replace(/ /g, '_').toLowerCase() //this.attributes.title.replace(/ /g, '_').toLowerCase()
        let fileName = 'statzon_' + chartTitle + '.xlsx'
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.sendGoogleAnalyticsEvent('download_datasets_excel', this.$store.getters['search/getResults'].dataset.byId[this.$store.getters['search/getSelectedHash']].attributes.title, false, this.$store.getters['user/getSubscriptionStatus'])
      }).catch(error => {
        this.excelError = error.errors
      })
    },
    toggleTimeline (key) {
      this.$store.dispatch('search/setSelectedChartTimeline', key.name)
    },
    toggleLabel (key) {
      let index = this.unselectedImageLabels.indexOf(key)
      if (index === -1) {
        this.unselectedImageLabels.push(key)
      } else {
        this.unselectedImageLabels.splice(index, 1)
      }
    },
    close () {
      let searchView = this.comingFrom === 'Datasets' ? 'datasets' : 'search'

      this.unselectedImageLabels = []
      this.$store.dispatch('app/setSettings', { 'name': 'search.view', 'value': searchView })

      if (searchView === 'search') {
        this.$router.push('/search?' + this.$store.getters['search/getFiltersQuery'](false, false))
      } else {
        this.$router.push('/datasets?' + this.$store.getters['search/getFiltersQuery'](false, false) + this.$store.getters['search/getControlsQuery']('datasets', 'client'))
      }
    },
    itemClicked (value) {
      if (value.type !== 'news') {
        this.$store.dispatch('search/setSelectedView', value.type)
        this.$store.dispatch('search/setSelectedId', value.id)
        this.$store.dispatch('search/setSelectedHash', value.hash)
        this.$router.push('/'+ value.type + '/' + value.id)
      }
    },
    toggleControls (setting, value) {
      if (setting !== 'dataset.download' && setting !== 'dataset.embed') {
        this.$store.dispatch('app/setSettings', { 'name': setting, 'value': value })
      }

      if (setting === 'dataset.download') {
        if (value === 'xls') {
          this.downloadExcel()
        } else if (value === 'img') {
          this.$store.dispatch('app/setIsDownloadingImage', true)
        }
      }

      if (setting === 'dataset.embed') {
        if (!this.results.dataset.byId[this.selectedHash].attributes.embed) {
          this.$store.dispatch('search/createDatasetEmbed', this.results.dataset.byId[this.selectedHash].id).then(response => {
            this.$store.dispatch('search/setAddingEmbed', response.id)
          })
        } else {
          this.$store.dispatch('search/setAddingEmbed', this.results.dataset.byId[this.selectedHash].attributes.embed.id)
        }
      }

      if (setting === 'datasets.project') {
        this.$store.dispatch('search/setAddingToProject', {
          id: this.results.dataset.byId[this.selectedHash].id,
          type: 'datasets'
        })
      }

      if (setting === 'search.view') {
        if (['datasetTopics', 'datasetProviders'].includes(value)) {
          let url = value === 'datasetTopics' ? this.url['datasetTopics'] : this.url['datasetProviders']
          this.$router.push('/datasets?' + url)
        }
      }
    },
    resetResults () {
      let resetResults = this.$store.getters['search/getResults']
      delete resetResults.datasetTopics
      delete resetResults.datasetProviders
      delete resetResults.datasetPdf

      this.loading = {
        datasetTopics: true,
        datasetProviders: true,
        datasetPdf: true
      }

      this.$store.dispatch('search/setResults', resetResults)
    },
    async init () {
      let datasetId = this.$route.params.id
      let datasetHash = 'DATASET_' + datasetId

      this.$store.dispatch('app/setSettings', { 'name': 'search.view', 'value': 'dataset' })
      this.$store.dispatch('app/setSettings', { 'name': 'dataset.display', 'value': 'ver_bar' })
      this.$store.dispatch('search/setSelectedId', datasetId)
      this.$store.dispatch('search/setSelectedHash', datasetHash)
      this.$store.dispatch('search/setSelectedView', 'dataset')
      this.$store.dispatch('search/logView', datasetHash)

      if(!(this.results && this.results.dataset && this.results.dataset.byId[datasetHash])) {
        let newResult = false

        if (this.results && this.results.datasets && this.results.datasets.byId[datasetHash]) {
          newResult = this.results.datasets.byId[datasetHash]
        } else if (this.results && this.results.datasetTopics && this.results.datasetTopics.byId[datasetHash]) {
          // TODO: This is not working, its never found for some reason...
          // Upper one works sometimes when actual search results (datasets) are same as datasetTopics (coming from search, not F5)
          newResult = this.results.datasetTopics.byId[datasetHash]
        }

        if (newResult !== false) {
          let normalizedResults = this.$store.getters['search/getResults']

          normalizedResults.dataset = {
            'byId': { [datasetHash]: newResult },
            'allIds': [datasetHash]
          }

          this.$store.dispatch('search/setResults', normalizedResults)
        } else {
          await this.$store.dispatch('search/fetchDataset', datasetId)
        }
      }

      this.loaded = true
      let result = this.results.dataset.byId[datasetHash]

      if (datasetId !== this.results.dataset.byId[this.results.dataset.allIds[0]].id) {
        datasetId = this.results.dataset.byId[this.results.dataset.allIds[0]].id
        this.$router.push({ path: datasetId})
      }

      // SEO Title and description
      document.title = result.attributes.title;
      if (result.attributes.definition && result.attributes.definition !== '') {
        document.querySelector('meta[name="description"]').setAttribute("content", result.attributes.definition);
      } else if (result.attributes.topics && result.attributes.topics.length > 0) {
        document.querySelector('meta[name="description"]').setAttribute("content", 'Gain market insights about ' + result.attributes.topics[0].attributes.name);
      }
      var link = !!document.querySelector("link[rel='canonical']") ? document.querySelector("link[rel='canonical']") : document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', 'https://app.statzon.com/datasets/' + result.id);
      document.head.appendChild(link);

      this.url.datasetTopics = 'topics=' + result.attributes.topics[0].id + '&sort=relevance&page=1&mode=related&'
      this.url.datasetProviders = 'providers=' + result.attributes.providers[0].id + '&sort=-published&page=1&mode=related&'
      this.url.datasetPdf = 'datasets/' + result.id + '/related?'

      this.delay(500).then(() => this.search('datasetPdf', 'datasetPdf', this.url.datasetPdf))
      this.delay(1000).then(() => this.search('datasetTopics', 'datasetTopics', 'datasets?' + this.url.datasetTopics))
      this.delay(1500).then(() => this.search('datasetProviders', 'datasetProviders', 'datasets?' + this.url.datasetProviders))
    },
    delay (time) {
      return new Promise(resolve => setTimeout(resolve, time))
    },
    onResize() {
      this.windowWidth = window.innerWidth
    },
    changeTab (value) {
      this.tabIndex = value
    },
    ...helperMethods
  },
  watch: {
    $route (to, from) {
      this.loaded = false
      this.comingFrom = from.name
      this.resetResults()
      this.init()
    },
    /*isAuthenticated: function (per, current) {
      let response = this.$store.dispatch('search/fetchResults')
      if (response) {
        let that = this
        this.$nextTick(function () {
          that.refreshKey = '_' + Math.random().toString(36).substr(2, 9)
        })
      }
    }*/
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.comingFrom = from.name
    })
  },
  mounted () {
    this.resetResults()
    this.init()

    let that = this
    document.addEventListener('keyup', function (event) {
      if (event.keyCode === 27) {
        that.close()
      }
    })

    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize)
    })
  },
  beforeDestroy () {
    window.removeEventListener('keyup', this.nextItem)
    window.removeEventListener('resize', this.nextItem)
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.wrap {
  width: 100%;
  max-width: $xlContentWidth;
  height: auto;
  margin: 144px auto 64px auto;

  @media screen and (min-width: $breakpointSm) {
    width: calc(100% - 32px);
    margin: 100px auto 464px auto;
  }

  @media screen and (min-width: $breakpointLg) {
    margin: 108px auto 464px auto;
  }

  &--loading {
    height: 100vh;
  }
}

.dataset {
  @include font('content--md');
  width: calc(100% - 2px);
  border-width: 1px;
  border-style: solid;
  border-color: $black;
  box-shadow: 0px 7px 8px -4px rgba(0, 0, 0, 0.2), 0px 12px 17px 2px rgba(0, 0, 0, 0.14), 0px 5px 22px 4px rgba(0, 0, 0, 0.12);
  position: relative;

  &__toggle {
    position: relative;
    margin: auto;
    display: inline-block;
    margin-top: 20px;
    left: 50%;
    transform: translate(-50%, 0);
    &>div, &>span {
      display: inline;
      margin: 0 5px;
    }
    &>span {
      position: relative;
      top: -8px;
    }
  }

  &__toggle_info {
    text-align: center;
  }

  &__legend_rows {
    position: relative;
    display: block;
    &>span {
      display: block;
      position: relative;
      text-align: center;
      margin: 5px 0;
    }
    &>div {
      display: inline-block;
      position: relative;
      left: 50%;
      transform: translate(-50%, 0);
    }
  }

  &__imagewrap {
    width: 100%;
    max-width: 1200px;
    margin: 20px auto;
    border-top: 1px solid $black;
    border-bottom: 1px solid $black;
    &_scroll {
      width: 1200px;
    }
    @media screen and (max-width: 1250px) {
      overflow-x: scroll;
    }

    &::-webkit-scrollbar {
      width: 18px;
      height: 20px;
    }

    &::-webkit-scrollbar-track:horizontal {
      background-color: $bg-2;
      border-top: 1px solid $black;
    }

    &::-webkit-scrollbar-track:vertical {
      background-color: $bg-2;
      border-left: 1px solid $black;
    }

    ::-webkit-scrollbar-corner {
      background-color: $bg-2;
      border-top: 1px solid $black;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 999px;
      border: 5px solid transparent;
      background-clip: content-box;
      background-color: rgba($black, 0.5);
    }

    &::-webkit-scrollbar-thumb:hover {
      border: 3px solid transparent;
    }

    &::-webkit-scrollbar-thumb:active {
      background-color: $black;
    }
  }

  &__image_download {
    background: $bg-3;
    &_button {
      display: block;
      margin: 20px auto 0 auto;
    }
  }

  &__title {
    align-self: center;
    background: transparent;
    padding: 12px;
    background: $bg-2;

    h1 {
      @include font('header--md');

      @media screen and (min-width: $breakpointMd) {
        overflow: hidden;
        display: -webkit-box;
        text-overflow: ellipsis;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
      }

      @media screen and (min-width: $breakpointLg) {
        @include font('header--lg');
      }
    }
  }

  &__close {
    position: absolute;
    top: 5px;
    right: 5px;
    display: inline-block;
  }

  &__chart_view {
    position: relative;
    z-index: 20;
    //background-color: $bg-1;
  }

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg');
  }

  &__source {
    background: $bg-2;
    padding: 1rem 14px 0 14px;
  }

  &__attribute {
    color: rgba($white, 0.7);
  }

  &__value {
    color: rgba($white, 1);
  }

  &__controls {
    display: flex;
    justify-content: space-between;
    background-color: $bg-4;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    height: 36px;

    @media screen and (min-width: $breakpointLg) {
      height: 40px;
    }
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: left;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    height: 36px;

    @media screen and (min-width: $breakpointLg) {
      height: 40px;
    }
  }

  &__chart {
    background-color: $bg-2;
    //border-top: 1px solid rgba($white, 0.1);
    //border-bottom: 1px solid $black;
    position: relative;
  }

  &__datatable {
    background-color: $bg-2;
    //border-top: 1px solid rgba($white, 0.1);
    position: relative;
  }

  &__footer {
    width: 100%;
    height: 16px;
    background-color: $bg-1;
  }

  &__content {
    border-bottom: 1px solid $black;
  }

  &__panel {
    overflow: auto;
    position: absolute;
    top: 0px;
    z-index: 90;
    width: 100%;
    max-width: 540px;

    @media screen and (min-width: 540px) {
      //top: 16px;
      left: calc(50% - 270px);
    }

    &--success {
      overflow: visible;
    }
  }
}

.image_legend {
  &__items {
    text-align: center;
    background-color: $bg-2;
  }
  
  &__item {
    display: inline-block;
    align-items: center;
    padding: 2px 0px;
    user-select: none;
    cursor: pointer;

    &:hover, &:active, &:focus {
      background-color: $bg-4;
    }
  }

  &__icon {
    vertical-align: top;
    display: inline-block;
    background-color: $blue;
    width: 1rem;
    height: 1rem;
    margin: 0.25rem 0.5rem;
    border-radius: 4px;
    transition: transform 0.2s, background-color 0.2s;
  }

  &__name {
    vertical-align: top;
    display: inline-block;
    margin-right: 0.5rem;
  }
}

</style>
